import {Component, EventEmitter, Input, OnChanges, Output} from '@angular/core';

import * as moment from 'moment/moment';

import {EmpVariableAttributes, PayPeriodAttributes} from '../../../../../models/models.interfaces';
import {Data} from '../../../../../shared/api/server-responses.interfaces';
import {Contract} from '../../../../../shared/employees/employees.model';
import {isPresent} from '../../../../../shared/utils/lang';
import sumBy from 'lodash-es/sumBy';
import { EmployeesUtilsService } from '../../../../../shared/employees/employees-utils.service';
import { tap } from 'rxjs/operators';

const hasNewElements = (elements: Data<EmpVariableAttributes>[]) => elements.some((element: Data<EmpVariableAttributes>) => element.isNew);


@Component({
  selector: 'gp-variable-elements-multiple-payperiod-row',
  templateUrl: './variable-elements-multiple-payperiod-row.component.html',
  styleUrls: ['./variable-elements-multiple-payperiod-row.component.scss']
})
export class VariableElementsMultiplePayperiodRowComponent implements OnChanges {
  @Input()
  public editedPayPeriod: Data<PayPeriodAttributes>;

  @Input()
  public previousPayPeriod: Data<PayPeriodAttributes>;

  @Input()
  public nextPayPeriod: Data<PayPeriodAttributes>;

  @Input()
  public variableElements: Data<EmpVariableAttributes>[] = [];

  @Input()
  public type: string;

  @Input()
  public readOnly = false;

  @Input()
  public contract: Contract;

  @Output()
  public onEditElement: EventEmitter<Data<EmpVariableAttributes>> = new EventEmitter<Data<EmpVariableAttributes>>();

  @Output()
  public viewElement: EventEmitter<Data<EmpVariableAttributes>> = new EventEmitter<Data<EmpVariableAttributes>>();

  @Output()
  public variableElementDeletedNew: EventEmitter<{
    item: Data<EmpVariableAttributes>,
    isNested: boolean,
    type: string
  }> = new EventEmitter();

  @Output()
  public variableElementDeleted: EventEmitter<{
    item: Data<EmpVariableAttributes>,
    isNested: boolean,
    type: string
  }> = new EventEmitter();

  @Output()
  public isDirty: EventEmitter<Data<EmpVariableAttributes>> = new EventEmitter();

  public expanded = true;
  public isNewNode = false;

  public editableElements: Data<EmpVariableAttributes>[];
  public previousValue: number | undefined;
  public editedValue: number | undefined;
  public nextValue: number | undefined;
  public previousIsSum: boolean;
  public nextIsSum: boolean;
  public previousComment: string;
  public nextComment: string;
  public overflowBack = false;
  public overflowAhead = false;

  public isEmployeeFormEmbedded = false;

  constructor(
    private employeesUtilsService: EmployeesUtilsService
  ) {
    this.employeesUtilsService.isEmployeeFormEmbedded$.pipe(
      tap(isEmbedded => {
        this.isEmployeeFormEmbedded = isEmbedded;
      })
    ).subscribe();
  }

  public ngOnChanges() {
    this.editableElements = this.getElementsForPayPeriod(this.editedPayPeriod);
    this.isNewNode = hasNewElements(this.editableElements);
    if (this.editableElements.length) {
      this.expanded = true;
      this.editableElements[0].editable = true;
    }

    const previousElements = this.getElementsForPayPeriod(this.previousPayPeriod);
    if (this.editableElements[0]?.id && previousElements.map(x => x.id).includes(this.editableElements[0].id)) {
      this.overflowBack = true;
    }
    this.previousIsSum = previousElements.length > 1;
    this.previousValue = this.getValueForElements(previousElements);
    this.previousComment = previousElements.length === 1 ? previousElements[0].attributes.comment : '';

    const nextElements = this.getElementsForPayPeriod(this.nextPayPeriod);
    if (this.editableElements[0]?.id && nextElements.map(x => x.id).includes(this.editableElements[0].id)) {
      this.overflowAhead = true;
    }
    this.nextIsSum = nextElements.length > 1;
    this.nextValue = this.getValueForElements(nextElements);
    this.nextComment = nextElements.length === 1 ? nextElements[0].attributes.comment : '';

    this.editedValue = this.getValueForElements(this.editableElements);
  }

  private getValueForElements(elements: Data<EmpVariableAttributes>[]): number | undefined {
    const validValues = elements
      .map(element => element.attributes.value)
      .filter(value => value !== undefined);

    return validValues.length === 0
      ? undefined
      : validValues.reduce((sum, value) => +value + sum, 0);
  }

  private getElementsForPayPeriod(payPeriod: Data<PayPeriodAttributes>) {
    if (!isPresent(payPeriod)) {
      return [];
    }
    return this.variableElements.filter(variable =>
      moment(variable.attributes.enddate).isSameOrAfter(moment(payPeriod.attributes.begindate))
      && moment(variable.attributes.begindate).isSameOrBefore(moment(payPeriod.attributes.enddate)));
  }

  public markAsDirty(element: Data<EmpVariableAttributes>): void {
    this.isDirty.emit(element);
  }

  public elementDeleted(element: Data<EmpVariableAttributes>, isNested: boolean, type: string): void {
    const _element = {item: element, isNested, type};
    if (element.isNew) {
      this.variableElementDeletedNew.emit(_element);
    } else {
      this.variableElementDeleted.emit(_element);
    }
  }

  public editVariable(variable: Data<EmpVariableAttributes>): void {
    this.onEditElement.emit(variable);
  }

  public toggleExpand(): void {
    this.expanded = !this.expanded;
  }

  public getVariableValue(variable: Data<EmpVariableAttributes>, payPeriod: Data<PayPeriodAttributes>) {
    if (!payPeriod) {
      return '';
    }
    const inPeriod = moment(variable.attributes.enddate).isSameOrAfter(moment(payPeriod.attributes.begindate))
      && moment(variable.attributes.begindate).isSameOrBefore(moment(payPeriod.attributes.enddate));

    if (inPeriod) {
      return variable.attributes.value;
    } else {
      return '';
    }
  }

  public getVariableSum(variables: Data<EmpVariableAttributes>[]): number {
    return sumBy(variables, (v: Data<EmpVariableAttributes>) => +v.attributes?.value || 0);
  }

  public getActionTooltip(variable: Data<EmpVariableAttributes>): string {
    if (this.readOnly) {
      return 'TOOLTIPS.view';
    }

    return variable.attributes.value ? 'TOOLTIPS.edit' : 'NOTIFICATIONS.add'
  }
}
